import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CourseCard } from "@/components/CourseCard";
import { Loader2, GraduationCap, CalendarClock, BookOpen, MessageSquare, ShieldAlert, ShieldCheck, FileText, Play, CalendarPlus } from "lucide-react";

const GRADE_LABEL = (g) => (g === "K" ? "Kindergarten" : `Grade ${g}`);
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");

export default function Dashboard() {
  const { user, access } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [continueItems, setContinueItems] = useState([]);
  const [extending, setExtending] = useState(null);
  const [cover, setCover] = useState(null);

  useEffect(() => {
    api.get("/me/continue").then((r) => setContinueItems(r.data)).catch(() => {});
    api.get("/cover-school/me").then((r) => setCover(r.data)).catch(() => {});
  }, []);

  const extend = async (enrollmentId) => {
    setExtending(enrollmentId);
    try {
      const { data } = await api.post("/checkout/extension", { enrollment_id: enrollmentId, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setExtending(null);
    }
  };

  const [managing, setManaging] = useState(false);
  const manageSubscription = async () => {
    setManaging(true);
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setManaging(false);
    }
  };

  useEffect(() => {
    if (!access) return;
    (async () => {
      setLoading(true);
      let list = [];
      if (access.has_membership) {
        const { data } = await api.get("/courses", { params: { grade: "K" } });
        list = data;
      } else if (access.grades?.length) {
        const results = await Promise.all(access.grades.map((g) => api.get("/courses", { params: { grade: g } })));
        list = results.flatMap((r) => r.data);
      }
      setCourses(list.slice(0, 6));
      setLoading(false);
    })();
  }, [access]);

  const hasAccess = access && (access.has_membership || access.grades?.length > 0);

  return (
    <div className="mx-auto max-w-7xl px-5 py-12 lg:px-8">
      <div className="border-b border-border pb-8">
        <span className="label-caps text-primary">Members Area</span>
        <h1 className="mt-2 font-head text-4xl font-black">Welcome, {user?.name?.split(" ")[0] || "friend"}.</h1>
        <p className="mt-2 text-muted-foreground">Manage your enrollments, jump into courses, and open the AI Tutor.</p>
      </div>

      {/* Access summary */}
      <div className="mt-8 grid gap-px border border-border bg-border md:grid-cols-3">
        <div className="bg-card p-6">
          <div className="flex items-center gap-2 text-primary"><CalendarClock className="h-5 w-5" /><span className="label-caps">Membership</span></div>
          {access?.has_membership ? (
            <>
              <p className="mt-3 font-head text-2xl font-bold text-foreground">Active</p>
              <p className="mt-1 text-sm text-muted-foreground">{access.membership?.student_count} student(s) · {access.membership?.is_subscription ? "renews" : "active through"} {fmtDate(access.membership?.expires_at)}</p>
              {access.membership?.can_manage && (
                <Button onClick={manageSubscription} disabled={managing} variant="outline" className="mt-3 rounded-none label-caps" data-testid="manage-subscription-btn">
                  {managing ? <Loader2 className="h-4 w-4 animate-spin" /> : "Manage / Cancel"}
                </Button>
              )}
            </>
          ) : (
            <p className="mt-3 text-sm text-muted-foreground">No active membership.</p>
          )}
        </div>
        <div className="bg-card p-6">
          <div className="flex items-center gap-2 text-primary"><GraduationCap className="h-5 w-5" /><span className="label-caps">Enrollments</span></div>
          <p className="mt-3 font-head text-2xl font-bold text-foreground">{access?.enrollments?.length || 0}</p>
          <p className="mt-1 text-sm text-muted-foreground">Active annual grade enrollments</p>
        </div>
        <div className="bg-card p-6">
          <div className="flex items-center gap-2 text-primary"><MessageSquare className="h-5 w-5" /><span className="label-caps">AI Tutor</span></div>
          <p className="mt-3 text-sm text-muted-foreground">Available anytime for enrolled students.</p>
          <Link to="/tutor"><Button className="mt-3 rounded-none label-caps bg-primary hover:bg-primary/80" data-testid="dash-tutor-btn">Open Tutor</Button></Link>
        </div>
      </div>

      {/* Continue where you left off */}
      {continueItems.length > 0 && (
        <div className="mt-10">
          <h2 className="font-head text-2xl font-bold">Continue Where You Left Off</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {continueItems.map((item) => (
              <Link
                key={item.lesson_id}
                to={`/lesson/${item.lesson_id}`}
                data-testid={`continue-${item.lesson_id}`}
                className="group flex items-center justify-between gap-3 border border-border bg-card p-5 transition-all hover:-translate-y-1 hover:border-primary hover:shadow-brutal"
              >
                <div className="min-w-0">
                  <span className="label-caps text-[0.6rem]" style={{ color: item.course?.subject_color }}>{item.course?.subject_name}</span>
                  <p className="mt-1 truncate font-head text-lg font-bold group-hover:text-primary">{item.lesson_title}</p>
                  <p className="truncate text-xs text-muted-foreground">{item.course?.title}</p>
                </div>
                <span className={`flex h-10 w-10 shrink-0 items-center justify-center border ${item.status === "completed" ? "border-primary text-primary" : "border-border text-muted-foreground group-hover:border-primary group-hover:text-primary"}`}>
                  <Play className="h-4 w-4" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Enrollment list */}
      {access?.enrollments?.length > 0 && (
        <div className="mt-10">
          <h2 className="font-head text-2xl font-bold">Your Enrollments</h2>
          <div className="mt-4 border border-border">
            {access.enrollments.map((e, i) => (
              <div key={i} className="flex flex-wrap items-center justify-between gap-3 border-b border-border p-5 last:border-0" data-testid={`enrollment-${i}`}>
                <div>
                  <p className="font-head text-lg font-bold">{e.student_name} — {GRADE_LABEL(e.grade_level)}</p>
                  <p className="text-sm text-muted-foreground">Active through {fmtDate(e.expires_at)}</p>
                </div>
                <div className="flex gap-2">
                  {e.enrollment_id && (
                    <Button onClick={() => extend(e.enrollment_id)} disabled={extending === e.enrollment_id} variant="outline" className="rounded-none label-caps border-primary/50 text-primary hover:bg-primary/10" data-testid={`extend-${i}`}>
                      {extending === e.enrollment_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CalendarPlus className="mr-2 h-4 w-4" /> Extend</>}
                    </Button>
                  )}
                  <Link to={`/curriculum?grade=${e.grade_level}`}><Button variant="outline" className="rounded-none label-caps">View Grade</Button></Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Cover School documents */}
      {cover?.enrolled && (
        <div className="mt-10" data-testid="cover-school-section">
          <div className="flex items-center gap-2 text-primary"><ShieldCheck className="h-5 w-5" /><h2 className="font-head text-2xl font-bold text-foreground">Cover School Documents</h2></div>
          <p className="mt-1 text-sm text-muted-foreground">Hornsby Church School · {cover.school_year} · active through {fmtDate(cover.expires_at)}</p>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            {cover.documents?.map((d) => (
              <Link key={d.type} to={`/cover-school/document/${d.type}`} data-testid={`cover-doc-${d.type}`} className="group flex items-center justify-between gap-3 border border-border bg-card p-5 transition-all hover:-translate-y-1 hover:border-primary hover:shadow-brutal">
                <div className="flex items-center gap-3"><FileText className="h-5 w-5 text-primary" /><span className="font-bold group-hover:text-primary">{d.title}</span></div>
                <span className="label-caps text-primary">View →</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Courses */}
      <div className="mt-12">
        <div className="flex items-center justify-between">
          <h2 className="font-head text-2xl font-bold">{hasAccess ? "Continue Learning" : "Get Started"}</h2>
          <Link to="/curriculum/browse" className="label-caps text-primary hover:underline">All Courses →</Link>
        </div>

        {!hasAccess ? (
          <div className="mt-6 border border-border bg-card p-10 text-center" data-testid="no-access-cta">
            <ShieldAlert className="mx-auto h-10 w-10 text-primary" />
            <h3 className="mt-4 font-head text-2xl font-bold">No Active Access Yet</h3>
            <p className="mx-auto mt-2 max-w-md text-muted-foreground">Enroll a student for a full year, or start a monthly family membership to unlock the curriculum.</p>
            <Link to="/pricing"><Button className="mt-6 rounded-none label-caps bg-primary hover:bg-primary/80" data-testid="dash-enroll-btn">View Enrollment Options</Button></Link>
          </div>
        ) : loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
        ) : (
          <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {courses.map((c) => <CourseCard key={c.course_id} course={c} />)}
          </div>
        )}
      </div>
    </div>
  );
}
